<template>
  <main class="appointment-settings">
    <router-link
      v-bind:to="{ name: 'ClinicPanelEmployeeList' }"
      class="appointment-settings__button-back"
    >
      Назад
    </router-link>
    <h1 class="appointment-settings__title">Расписание</h1>
    <div class="appointment-settings__color-mode">Отображение цвета записей в расписании</div>
    <input-radio-with-bullets
      id="button-edit-view-mode-parent"
      identifier="appointment-mode-color"
      v-bind:items="appointmentModeColorOptions"
      v-bind:value="selectedAppointmentModeColor"
      v-on:input="onInputAppointmentModeColor"
    />
    <table
      v-if="selectedAppointmentModeColor === $options.colorModeByCabinets"
      class="appointment-settings__table"
    >
      <thead>
        <tr class="appointment-settings__table__head">
          <th class="appointment-settings__table__cell">Кресло</th>
          <th class="appointment-settings__table__cell color">Цвет</th>
        </tr>
      </thead>
      <tbody>
        <tr
          class="appointment-settings__table__row"
          v-for="cabinet in cabinets"
          v-bind:key="cabinet.id"
        >
          <td class="appointment-settings__table__cell">{{ cabinet.name }}</td>
          <popover-menu overflow-auto v-model="showColorsMenu[cabinet.id]">
            <template v-slot:trigger="{ listenerObject }">
              <td class="appointment-settings__table__cell color">
                <button
                  v-bind:style="{ backgroundColor: cabinet.color }"
                  class="appointment-settings__color"
                  id="edit-button-the-color-parent"
                  type="button"
                  v-on="listenerObject"
                />
              </td>
            </template>
            <color-picker
              v-bind:key="showColorsMenu[cabinet.id]"
              v-bind:initial-color="cabinet.color"
              v-bind:presets="colorOptions"
              v-on:change="onChangeCabinetColor(cabinet.id, $event)"
            />
          </popover-menu>
        </tr>
      </tbody>
    </table>
    <table
      v-if="selectedAppointmentModeColor === $options.colorModeByEmployees"
      class="appointment-settings__table"
    >
      <thead>
        <tr class="appointment-settings__table__head">
          <th class="appointment-settings__table__cell">Сотрудник</th>
          <th class="appointment-settings__table__cell color">Цвет</th>
        </tr>
      </thead>
      <tbody>
        <tr
          class="appointment-settings__table__row"
          v-for="employee in employees"
          v-bind:key="employee.id"
        >
          <td class="appointment-settings__table__cell">{{ employee.name }}</td>
          <popover-menu overflow-auto v-model="showColorsMenu[employee.id]">
            <template v-slot:trigger="{ listenerObject }">
              <td class="appointment-settings__table__cell color">
                <button
                  v-bind:style="{ backgroundColor: employee.color }"
                  class="appointment-settings__color"
                  id="edit-button-the-color-parent"
                  type="button"
                  v-on="listenerObject"
                />
              </td>
            </template>
            <color-picker
              v-bind:key="showColorsMenu[employee.id]"
              v-bind:initial-color="employee.color"
              v-bind:presets="colorOptions"
              v-on:change="onChangeEmployeeColor(employee.id, $event)"
            />
          </popover-menu>
        </tr>
      </tbody>
    </table>
    <on-boarding-dialog
      :is-page-ready="isLoaded"
      :is-show-screen="isShowOnBoardingDialog"
      :is-show-button-schedule-view-select-mode="isShowButtonEditViewMode"
      :is-show-button-schedule-view-select-the-color="isShowButtonEditTheColor"
      @showed="showedOnboarding"
    >
      <input-radio-with-bullets
        v-if="isShowButtonEditViewMode"
        id="button-edit-view-mode-child"
        class="button-edit-view-mode-child"
        identifier="appointment-mode-color"
        v-bind:items="appointmentModeColorOptions"
        v-bind:value="'by-cabinets'"
      />
      <button
        v-if="isShowButtonEditTheColor"
        v-bind:style="{ backgroundColor: firstColor }"
        class="appointment-settings__color"
        id="edit-button-the-color-child"
        type="button"
      />
    </on-boarding-dialog>
    <next-step-setting-instruction
      v-bind:current-route-name="$route.name"
      v-bind:available-steps="instructionSteps"
    />
  </main>
</template>

<script>
import ColorPicker from '@/components/ColorPicker.vue';
import InputRadioWithBullets from '@/components/InputRadioWithBullets.vue';
import notifierMixin from '@/mixins/notifierMixin';
import PopoverMenu from '@/components/PopoverMenu.vue';
import OnBoardingDialog from '@/components/OnBoardingDialog.vue';
import onBoardingShowMixin from '@/mixins/onBoardingShowMixin';
import onBoardingMixin from '@/mixins/onBoardingMixin';
import NextStepSettingInstruction from '@/components/NextStepSettingInstruction.vue';
import settingInstructionMixin from '@/mixins/settingInstructionMixin';

export default {
  colorModeByCabinets: 'by-cabinets',
  colorModeByEmployees: 'by-employees',
  defaultColor: '#D2EDB0',
  components: {
    ColorPicker,
    NextStepSettingInstruction,
    OnBoardingDialog,
    InputRadioWithBullets,
    PopoverMenu,
  },
  computed: {
    /**
     * @description Получить первый цвет
     * @returns {string}
     */
    firstColor() {
      if (this.selectedAppointmentModeColor === this.$options.colorModeByCabinets) {
        return this.cabinets[0]?.color ?? this.$options.defaultColor;
      }
      return this.employees[0]?.color ?? this.$options.defaultColor;
    },
    /**
     * Показываем ли окно Онбординга
     *
     * @return {boolean}
     */
    isShowOnBoardingDialog() {
      return this.isShowButtonEditTheColor
        || this.isShowButtonEditViewMode;
    },
    /**
     * Показываем представление "Изменить режим отображения"
     *
     * @return {boolean}
     */
    isShowButtonEditViewMode() {
      return this.step?.notShowedView === 'select-view-schedule-mode';
    },
    /**
     * Показываем представление "Изменить цвет"
     *
     * @return {boolean}
     */
    isShowButtonEditTheColor() {
      return this.step?.notShowedView === 'select-the-color'
        && (this.employees.length > 0 || this.cabinets.length > 0);
    },
  },
  created() {
    this.fetchInstructionSteps();
    const onSuccess = (response) => {
      this.appointmentModeColorOptions = response.appointment_color_modes;
      response.appointment_color_modes.forEach((mode) => {
        if (mode.tags.includes('selected')) {
          this.selectedAppointmentModeColor = mode.value;
        }
      });
      this.cabinets = response.cabinets;
      this.colorOptions = response.colors;
      this.employees = response.employees;
      this.isLoaded = true;
    };
    const onFail = (error) => {
      this.notifyFail(error.title);
    };
    this
      .$appointmentScheduleGateway
      .viewSettings()
      .then((result) => result.fold(onFail, onSuccess));
  },
  data() {
    return {
      /**
       * Слаг шага Онбординга
       * @type {string}
       */
      stepSlug: 'view-schedule',
      appointmentModeColorOptions: [],
      cabinets: [],
      colorOptions: [],
      employees: [],
      isAwaitRequest: false,
      isLoaded: false,
      selectedAppointmentModeColor: '',
      showColorsMenu: [],
    };
  },
  methods: {
    /**
     * Обрабатываем события показа представления Онбординга
     *
     * @return void
     */
    async showedOnboarding(slugStep) {
      await this.setStepByStepSlug(slugStep);
    },
    onChangeCabinetColor(cabinetId, color) {
      if (this.isAwaitRequest) { return; }
      this.isAwaitRequest = true;
      const onSuccess = (response) => {
        this.showColorsMenu[cabinetId] = false;
        this.cabinets = this.cabinets.map((candidate) => {
          if (candidate.id === cabinetId) {
            return { ...candidate, color };
          }
          return candidate;
        });
        this.notifySuccess(response.message);
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this
        .$appointmentScheduleGateway
        .updateCabinetColor(cabinetId, color)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onChangeEmployeeColor(employeeId, color) {
      if (this.isAwaitRequest) { return; }
      this.isAwaitRequest = true;
      const onSuccess = (response) => {
        this.showColorsMenu[employeeId] = false;
        this.employees = this.employees.map((candidate) => {
          if (candidate.id === employeeId) {
            return { ...candidate, color };
          }
          return candidate;
        });
        this.notifySuccess(response.message);
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this
        .$appointmentScheduleGateway
        .updateEmployeeColor(employeeId, color)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onInputAppointmentModeColor(colorMode) {
      this.selectedAppointmentModeColor = colorMode;
      if (this.isAwaitRequest) { return; }
      this.isAwaitRequest = true;
      const onSuccess = (response) => {
        this.notifySuccess(response.message);
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this
        .$appointmentScheduleGateway
        .updateAppointmentSettings(colorMode)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
  },
  updated() {
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonEditViewMode,
      '#button-edit-view-mode-parent',
      '#button-edit-view-mode-child',
    );
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonEditTheColor,
      '#edit-button-the-color-parent',
      '#edit-button-the-color-child',
    );
  },
  mixins: [
    notifierMixin,
    onBoardingMixin,
    onBoardingShowMixin,
    settingInstructionMixin,
  ],
  name: 'ClinicPanelAppointmentSettings',
};
</script>

<style lang="scss">
.appointment-settings {
  width: 100%;
  padding: 38px 20px 160px 23px;
  overflow-y: auto;

  &__button-back {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    text-decoration: none;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  &__title {
    margin-bottom: 30px;
    color: var(--reading-color);
  }

  &__color-mode {
    font-weight: 600;
    margin-bottom: 10px;
  }

  &__color {
    width: 25px;
    height: 25px;
    border-radius: 50%;
  }

  &__table {
    margin-top: 10px;
    width: 100%;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    border-collapse: collapse;

    &__head {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
    &__row {
      border-bottom: 1px solid var(--fill-color);
    }
    &__cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: pre-line;
      &.color {
        text-align: center;
        width: 100px;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .appointment-settings {
    padding: 38px 10px 140px;
  }
}

</style>
